/* eslint-disable no-unused-vars */
import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';
import {getCurrencySymbol,getCurrencyCode} from '../utils/locale';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt=(v:any,type:'currency'|'number'|'percent'='currency')=>{
  const n=parseFloat(v||0);
  if(type==='currency') return n.toLocaleString('en-KE',{style:'currency',currency:getCurrencyCode(),maximumFractionDigits:0});
  if(type==='percent') return n.toFixed(1)+'%';
  return n.toLocaleString('en-KE');
};
const fmtK=(v:any)=>{const n=parseFloat(v||0);return n>=1000000000?`${getCurrencySymbol()}${(n/1000000000).toFixed(1)}B`:n>=1000000?`${getCurrencySymbol()}${(n/1000000).toFixed(1)}M`:n>=1000?`${getCurrencySymbol()}${(n/1000).toFixed(0)}K`:fmt(n);};
const pct=(a:any,b:any)=>{const av=parseFloat(a||0),bv=parseFloat(b||0);if(bv===0)return 0;return((av-bv)/bv*100).toFixed(1);};

// ─── mini chart bar ───────────────────────────────────────────────────────────
const MiniBar=({data,valueKey,labelKey,color='blue',height=80}:{data:any[],valueKey:string,labelKey:string,color?:string,height?:number})=>{
  if(!data?.length) return <div className="text-xs text-slate-300 text-center py-4">No data</div>;
  const max=Math.max(...data.map(d=>parseFloat(d[valueKey]||0)));
  const clr={blue:'bg-blue-500',green:'bg-green-500',amber:'bg-amber-500',red:'bg-red-500',purple:'bg-purple-500',teal:'bg-teal-500'}[color]||'bg-blue-500';
  return(
    <div className="flex items-end gap-0.5" style={{height}}>
      {data.slice(-20).map((d,i)=>{
        const h=max>0?Math.max(3,(parseFloat(d[valueKey]||0)/max)*height):3;
        return<div key={i} className="group relative flex-1 flex flex-col justify-end">
          <div className={`${clr} rounded-sm opacity-80 hover:opacity-100 transition-opacity`} style={{height:h}}/>
          <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-1.5 py-0.5 rounded hidden group-hover:block whitespace-nowrap z-10">{d[labelKey]}: {parseFloat(d[valueKey]||0).toLocaleString()}</div>
        </div>;
      })}
    </div>
  );
};

// ─── horizontal bar ───────────────────────────────────────────────────────────
const HBar=({data,valueKey,labelKey,color='blue',max:maxProp}:{data:any[],valueKey:string,labelKey:string,color?:string,max?:number})=>{
  if(!data?.length) return <div className="text-xs text-slate-300 py-2">No data</div>;
  const max=maxProp||Math.max(...data.map(d=>parseFloat(d[valueKey]||0)));
  const clr={blue:'bg-blue-500',green:'bg-green-500',amber:'bg-amber-500',red:'bg-red-500',purple:'bg-purple-500',teal:'bg-teal-500'}[color]||'bg-blue-500';
  return(
    <div className="space-y-1.5">
      {data.slice(0,10).map((d,i)=>{
        const pct=max>0?(parseFloat(d[valueKey]||0)/max)*100:0;
        return(
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="text-slate-500 w-28 truncate flex-shrink-0">{d[labelKey]||'—'}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div className={`${clr} h-full rounded-full`} style={{width:`${pct}%`}}/>
            </div>
            <span className="text-slate-600 font-medium w-16 text-right">{parseFloat(d[valueKey]||0).toLocaleString()}</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── KPI card ─────────────────────────────────────────────────────────────────
const KpiCard=({label,value,sub,icon,color,trend}:{label:string,value:any,sub?:string,icon:string,color?:string,trend?:number})=>{
  const bg={blue:'bg-blue-50 border-blue-200',green:'bg-green-50 border-green-200',amber:'bg-amber-50 border-amber-200',red:'bg-red-50 border-red-200',purple:'bg-purple-50 border-purple-200',slate:'bg-slate-50 border-slate-200',teal:'bg-teal-50 border-teal-200'}[color||'slate']||'bg-slate-50 border-slate-200';
  const txt={blue:'text-[#02357A]',green:'text-green-700',amber:'text-amber-700',red:'text-red-700',purple:'text-purple-700',slate:'text-slate-700',teal:'text-teal-700'}[color||'slate']||'text-slate-700';
  return(
    <div className={`card p-4 border ${bg} flex flex-col`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        {trend!==undefined&&<span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${trend>=0?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>{trend>=0?'↑':'↓'}{Math.abs(trend).toFixed(1)}%</span>}
      </div>
      <p className={`text-xl font-black ${txt} leading-tight`}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5 font-medium">{label}</p>
      {sub&&<p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
};

// ─── Section card ────────────────────────────────────────────────────────────
const Section=({title,icon,children,noPad}:{title:string,icon:string,children:React.ReactNode,noPad?:boolean})=>(
  <div className="card overflow-hidden">
    <div className="p-4 border-b bg-slate-50 flex items-center gap-2"><span>{icon}</span><h3 className="font-semibold text-slate-700">{title}</h3></div>
    <div className={noPad?'':' p-4'}>{children}</div>
  </div>
);

// ─── Table ────────────────────────────────────────────────────────────────────
const STable=({headers,rows}:{headers:string[],rows:any[][]})=>(
  !rows?.length?<div className="text-xs text-slate-300 py-3">No data</div>:
  <div className="overflow-x-auto">
    <table className="w-full text-xs">
      <thead><tr className="border-b">{headers.map(h=><th key={h} className="text-left p-2 text-slate-400 font-semibold">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r,i)=><tr key={i} className="border-b last:border-0 hover:bg-slate-50">{r.map((c,j)=><td key={j} className="p-2 text-slate-600">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const VIEWS=['executive','revenue','opd','ipd','emergency','lab','radiology','ot','pharmacy','financial','patient','doctor','clinical','insurance','bloodbank','inventory'];
const VIEW_LABELS:Record<string,string>={'executive':'📊 Executive','revenue':'💰 Revenue','opd':'🏥 OPD','ipd':'🛏️ IPD','emergency':'🚨 Emergency','lab':'🧪 Lab','radiology':'🩻 Radiology','ot':'🔪 OT','pharmacy':'💊 Pharmacy','financial':'📈 Financial','patient':'👥 Patients','doctor':'👨‍⚕️ Doctors','clinical':'🩺 Clinical','insurance':'🏦 Insurance','bloodbank':'🩸 Blood Bank','inventory':'📦 Inventory'};

export default function AnalyticsPage(){
  const [view,setView]=useState('executive');
  const [data,setData]=useState<any>(null);
  const [loading,setLoading]=useState(false);
  const [dateRange,setDR]=useState({from:new Date(Date.now()-30*86400000).toISOString().split('T')[0],to:new Date().toISOString().split('T')[0]});
  const [exec,setExec]=useState<any>(null);

  // Load executive once on mount
  useEffect(()=>{api.getAnalyticsDashboard().then(r=>setExec(r.data||{})).catch(()=>{});}, []);

  const loadView=useCallback(async()=>{
    setLoading(true);setData(null);
    const p={from:dateRange.from,to:dateRange.to};
    try{
      const apiMap:Record<string,()=>Promise<any>>={
        executive:()=>api.getAnalyticsDashboard(),
        revenue:()=>api.getAnalyticsRevenue(p),opd:()=>api.getAnalyticsOpd(p),ipd:()=>api.getAnalyticsIpd(p),emergency:()=>api.getAnalyticsEmergency(p),
        lab:()=>api.getAnalyticsLab(p),radiology:()=>api.getAnalyticsRadiology(p),ot:()=>api.getAnalyticsOt(p),pharmacy:()=>api.getAnalyticsPharmacy(p),
        financial:()=>api.getAnalyticsFinancial(p),patient:()=>api.getAnalyticsPatient(p),doctor:()=>api.getAnalyticsDoctor(p),
        clinical:()=>api.getAnalyticsClinical(p),insurance:()=>api.getAnalyticsInsurance(p),bloodbank:()=>api.getAnalyticsBloodBank(p),inventory:()=>api.getAnalyticsInventory(p),
      };
      const r=await apiMap[view]();setData(r.data||{});
    }catch{}finally{setLoading(false);}
  },[view,dateRange]);
  useEffect(()=>{const t=setTimeout(loadView,300);return()=>clearTimeout(t);},[loadView]);

  return(
    <Layout title="Analytics & Business Intelligence">
      {/* Top controls */}
      <div className="flex gap-3 items-center mb-4 flex-wrap">
        <div className="flex gap-1.5 items-center flex-wrap flex-1">
          <input type="date" className="input !w-36 text-xs" value={dateRange.from} onChange={e=>setDR({...dateRange,from:e.target.value})}/>
          <span className="text-slate-400 text-xs">to</span>
          <input type="date" className="input !w-36 text-xs" value={dateRange.to} onChange={e=>setDR({...dateRange,to:e.target.value})}/>
          {[['7d',7],['30d',30],['90d',90],['1y',365]].map(([l,d])=>(
            <button key={l} onClick={()=>setDR({from:new Date(Date.now()-(+d)*86400000).toISOString().split('T')[0],to:new Date().toISOString().split('T')[0]})} className="text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-[#02357A]">{l}</button>
          ))}
        </div>
        <button onClick={loadView} className="btn-primary text-sm">↻ Refresh</button>
      </div>

      {/* View tabs */}
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1 border-b">
        {VIEWS.map(v=><button key={v} onClick={()=>setView(v)} className={`px-3 py-1.5 rounded-t-lg text-xs font-medium whitespace-nowrap flex-shrink-0 ${view===v?'bg-[#02357A] text-white':'text-slate-500 hover:bg-slate-100'}`}>{VIEW_LABELS[v]||v}</button>)}
      </div>

      {loading&&<div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mr-3"/><span className="text-slate-500">Loading analytics...</span></div>}

      {!loading&&data&&(

        /* ════════════════ EXECUTIVE ════════════════ */
        view==='executive'?(<div className="space-y-5">
          {/* Live ticker */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {[['Today Revenue',fmtK(data.revenue?.today),'💰','green'],['Month Revenue',fmtK(data.revenue?.month),'📈','blue'],['Patients Today',fmt(data.patients?.today,'number'),'👥','blue'],['OPD Today',fmt(data.opd?.today,'number'),'🏥','teal'],['IPD Active',fmt(data.ipd?.current??data.ipd?.admissionsToday,'number'),'🛏️','purple'],['Emergency',fmt(data.emergency?.today,'number'),'🚨','red'],['Bed Occupancy',fmt(data.ipd?.bedOccupancy,'number')+'%','🏢','amber'],['Outstanding',fmtK(data.revenue?.outstanding),'⚠️','red']].map(([l,v,i,c])=>(
              <KpiCard key={l as string} label={l as string} value={v as string} icon={i as string} color={c as string}/>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Lab Orders Today" value={fmt(data.lab?.today,'number')} icon="🧪" color="blue"/>
            <KpiCard label="Radiology Today" value={fmt(data.radiology?.today,'number')} icon="🩻" color="purple"/>
            <KpiCard label="OT Today" value={fmt(data.ot?.today,'number')} icon="🔪" color="amber"/>
            <KpiCard label="Pharmacy Today" value={fmt(data.pharmacy?.today,'number')} icon="💊" color="teal"/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Year Revenue" value={fmtK(data.revenue?.year)} icon="🏆" color="green"/>
            <KpiCard label="New Patients (Month)" value={fmt(data.patients?.newThisMonth,'number')} icon="🆕" color="blue"/>
            <KpiCard label="Admissions Today" value={fmt(data.ipd?.admissionsToday,'number')} icon="📥" color="teal"/>
            <KpiCard label="Discharges Today" value={fmt(data.ipd?.dischargesToday,'number')} icon="📤" color="slate"/>
          </div>
        </div>)

        /* ════════════════ REVENUE ════════════════ */
        :view==='revenue'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Revenue" value={fmtK(data.totals?.revenue)} icon="💰" color="green"/>
            <KpiCard label="Prev Month" value={fmtK(data.totals?.prevMonthRevenue)} icon="📅" color="slate"/>
            <KpiCard label="Growth" value={`${parseFloat(data.totals?.growthPct||0).toFixed(1)}%`} icon={parseFloat(data.totals?.growthPct||0)>=0?'↑':'↓'} color={parseFloat(data.totals?.growthPct||0)>=0?'green':'red'} trend={parseFloat(data.totals?.growthPct||0)}/>
            <KpiCard label="Bills Count" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.bills||0),0),'number')} icon="🧾" color="blue"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Section title="Daily Revenue Trend" icon="📈" ><MiniBar data={data.daily||[]} valueKey="revenue" labelKey="date" color="green" height={100}/></Section>
            <Section title="Revenue by Department" icon="🏥"><HBar data={data.byDepartment||[]} valueKey="revenue" labelKey="department" color="blue"/></Section>
            <Section title="Payment Mode" icon="💳"><HBar data={data.byPaymentMode||[]} valueKey="revenue" labelKey="payment_method" color="teal"/></Section>
          </div>
        </div>)

        /* ════════════════ OPD ════════════════ */
        :view==='opd'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Visits" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.count||0),0),'number')} icon="🏥" color="blue"/>
            <KpiCard label="Completed" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.completed||0),0),'number')} icon="✅" color="green"/>
            <KpiCard label="No Show" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.no_show||0),0),'number')} icon="❌" color="red"/>
            <KpiCard label="No Show Rate" value={data.noShowRate+'%'} icon="📊" color="amber"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Section title="Daily OPD Trend" icon="📈"><MiniBar data={data.daily||[]} valueKey="count" labelKey="date" color="blue" height={100}/></Section>
            <Section title="Top Doctors" icon="👨‍⚕️"><HBar data={data.byDoctor||[]} valueKey="visits" labelKey="doctor_id" color="purple"/></Section>
            <Section title="Peak Hours" icon="⏰"><MiniBar data={data.byHour||[]} valueKey="count" labelKey="hour" color="amber" height={80}/></Section>
          </div>
          <Section title="Department-wise Visits" icon="🏥"><HBar data={data.byDepartment||[]} valueKey="visits" labelKey="department" color="teal"/></Section>
        </div>)

        /* ════════════════ IPD ════════════════ */
        :view==='ipd'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Admissions" value={fmt(data.admissionsTrend?.reduce((s:number,d:any)=>s+(+d.admissions||0),0),'number')} icon="📥" color="blue"/>
            <KpiCard label="Avg LOS (days)" value={data.alos||'—'} icon="📅" color="amber"/>
            <KpiCard label="Discharge Types" value={data.dischargeMode?.length||0} icon="📤" color="teal"/>
            <KpiCard label="Active Wards" value={data.bedUtilization?.length||0} icon="🛏️" color="purple"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Admission Trend" icon="📈"><MiniBar data={data.admissionsTrend||[]} valueKey="admissions" labelKey="date" color="blue" height={100}/></Section>
            <Section title="Ward Bed Utilization" icon="🛏️" noPad>
              <div className="divide-y">
                {(data.bedUtilization||[]).map((w:any)=>(
                  <div key={w.ward_name} className="px-4 py-2 flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-24 truncate">{w.ward_name||'—'}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden"><div className={`h-full rounded-full ${+w.occupancy_pct>90?'bg-red-500':+w.occupancy_pct>70?'bg-amber-500':'bg-green-500'}`} style={{width:`${w.occupancy_pct||0}%`}}/></div>
                    <span className="text-xs font-bold w-12 text-right">{w.occupancy_pct}%</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Admissions by Ward" icon="🏥"><HBar data={data.byWard||[]} valueKey="admissions" labelKey="ward_name" color="purple"/></Section>
            <Section title="Discharge Mode" icon="📤"><HBar data={data.dischargeMode||[]} valueKey="count" labelKey="discharge_type" color="teal"/></Section>
          </div>
        </div>)

        /* ════════════════ EMERGENCY ════════════════ */
        :view==='emergency'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Visits" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.count||0),0),'number')} icon="🚨" color="red"/>
            <KpiCard label="MLC Cases" value={fmt(data.mlcCount,'number')} icon="⚖️" color="amber"/>
            <KpiCard label="Avg Door-to-Doctor" value={data.avgDoorToDoctor?(data.avgDoorToDoctor+' min'):'—'} icon="⏱️" color="blue"/>
            <KpiCard label="Triage Categories" value={data.byTriage?.length||0} icon="🔴" color="slate"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Section title="Daily Trend" icon="📈"><MiniBar data={data.daily||[]} valueKey="count" labelKey="date" color="red" height={100}/></Section>
            <Section title="Triage Distribution" icon="🔴">
              <div className="space-y-2">
                {(data.byTriage||[]).map((t:any)=>{
                  const clr=t.triage_level==='CRITICAL'?'bg-red-600':t.triage_level==='URGENT'?'bg-amber-500':t.triage_level==='DOA'?'bg-slate-700':'bg-green-500';
                  const total=data.byTriage?.reduce((s:number,x:any)=>s+(+x.count||0),0)||1;
                  return<div key={t.triage_level} className="flex items-center gap-2 text-xs"><div className={`w-3 h-3 rounded-full ${clr} flex-shrink-0`}/><span className="text-slate-500 flex-1">{t.triage_level}</span><span className="font-bold">{t.count}</span><span className="text-slate-400">{Math.round((+t.count/+total)*100)}%</span></div>;
                })}
              </div>
            </Section>
            <Section title="Arrival Mode" icon="🚑"><HBar data={data.byArrival||[]} valueKey="count" labelKey="arrival_mode" color="blue"/></Section>
            <Section title="Disposition" icon="📤"><HBar data={data.byDisposition||[]} valueKey="count" labelKey="status" color="teal"/></Section>
          </div>
        </div>)

        /* ════════════════ LAB ════════════════ */
        :view==='lab'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Orders" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.orders||0),0),'number')} icon="🧪" color="blue"/>
            <KpiCard label="Revenue" value={fmtK(data.revenue)} icon="💰" color="green"/>
            <KpiCard label="Avg TAT" value={data.avgTatHours?(data.avgTatHours+'h'):'—'} icon="⏱️" color="amber"/>
            <KpiCard label="Pending Reports" value={fmt(data.pendingReports,'number')} icon="⏳" color="red"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Daily Order Trend" icon="📈"><MiniBar data={data.daily||[]} valueKey="orders" labelKey="date" color="blue" height={100}/></Section>
            <Section title="Top Test Panels" icon="🧫"><HBar data={data.byPanel||[]} valueKey="count" labelKey="panel_name" color="teal"/></Section>
          </div>
        </div>)

        /* ════════════════ RADIOLOGY ════════════════ */
        :view==='radiology'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Scans" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.count||0),0),'number')} icon="🩻" color="blue"/>
            <KpiCard label="Revenue" value={fmtK(data.revenue)} icon="💰" color="green"/>
            <KpiCard label="Avg Report TAT" value={data.avgTat?(data.avgTat+'h'):'—'} icon="⏱️" color="amber"/>
            <KpiCard label="Modalities" value={data.byModality?.length||0} icon="📊" color="purple"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Daily Scan Trend" icon="📈"><MiniBar data={data.daily||[]} valueKey="count" labelKey="date" color="purple" height={100}/></Section>
            <Section title="Modality Breakdown" icon="🏥">
              <div className="grid grid-cols-2 gap-2 p-2">
                {(data.byModality||[]).map((m:any)=>(
                  <div key={m.modality} className="bg-slate-50 p-3 rounded-lg text-center border">
                    <p className="text-xl font-bold text-[#02357A]">{m.count}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{m.modality}</p>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>)

        /* ════════════════ OT ════════════════ */
        :view==='ot'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Surgeries" value={fmt(data.daily?.reduce((s:number,d:any)=>s+(+d.count||0),0),'number')} icon="🔪" color="blue"/>
            <KpiCard label="Revenue" value={fmtK(data.revenue)} icon="💰" color="green"/>
            <KpiCard label="Utilization" value={data.utilizationPct+'%'} icon="📊" color="amber"/>
            <KpiCard label="Cancellations" value={fmt(data.byStatus?.find((s:any)=>s.status==='CANCELLED')?.count||0,'number')} icon="❌" color="red"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Section title="Daily Surgical Activity" icon="📈"><MiniBar data={data.daily||[]} valueKey="count" labelKey="date" color="amber" height={100}/></Section>
            <Section title="OT Status Distribution" icon="📊">
              <div className="space-y-2">
                {(data.byStatus||[]).map((s:any)=>{const total=data.byStatus?.reduce((acc:number,x:any)=>acc+(+x.count||0),0)||1;const p=Math.round((+s.count/total)*100);const clr=s.status==='COMPLETED'?'bg-green-500':s.status==='CANCELLED'?'bg-red-500':s.status==='IN_PROGRESS'?'bg-amber-500':'bg-blue-400';return<div key={s.status} className="flex items-center gap-2 text-xs"><div className={`w-3 h-3 rounded-full ${clr}`}/><span className="flex-1 text-slate-500">{s.status}</span><span className="font-bold">{s.count}</span><span className="text-slate-400">{p}%</span></div>;})}
              </div>
            </Section>
            <Section title="Top Surgeries" icon="🏆"><HBar data={data.topSurgeries||[]} valueKey="count" labelKey="procedure_name" color="blue"/></Section>
          </div>
        </div>)

        /* ════════════════ PHARMACY ════════════════ */
        :view==='pharmacy'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Revenue" value={fmtK(data.revenue)} icon="💊" color="green"/>
            <KpiCard label="Stock Value" value={fmtK(data.stockValue)} icon="📦" color="blue"/>
            <KpiCard label="Expiry Risk" value={fmt(data.expiryRisk?.length||0,'number')+' items'} icon="⚠️" color="red"/>
            <KpiCard label="Days Tracked" value={data.daily?.length||0} icon="📅" color="slate"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Daily Revenue Trend" icon="📈"><MiniBar data={data.daily||[]} valueKey="revenue" labelKey="date" color="green" height={100}/></Section>
            <Section title="Top 15 Drugs by Quantity" icon="💊"><HBar data={data.topDrugs||[]} valueKey="total_qty" labelKey="drug_name" color="blue"/></Section>
          </div>
          {data.expiryRisk?.length>0&&<Section title="Items Expiring Within 90 Days" icon="⚠️" noPad>
            <STable headers={['Drug Name','Batch','Qty','Expiry']} rows={(data.expiryRisk||[]).map((e:any)=>[e.name,e.batch_number,e.quantity,e.expiry_date?.slice(0,10)])}/>
          </Section>}
        </div>)

        /* ════════════════ FINANCIAL ════════════════ */
        :view==='financial'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Revenue" value={fmtK(data.summary?.totalRevenue)} icon="💰" color="green"/>
            <KpiCard label="Total Expenses" value={fmtK(data.summary?.totalExpenses)} icon="💸" color="red"/>
            <KpiCard label="Gross Profit" value={fmtK(data.summary?.grossProfit)} icon="📈" color={parseFloat(data.summary?.grossProfit||0)>=0?'green':'red'}/>
            <KpiCard label="Profit Margin" value={parseFloat(data.summary?.profitMargin||0).toFixed(1)+'%'} icon="📊" color={parseFloat(data.summary?.profitMargin||0)>=20?'green':parseFloat(data.summary?.profitMargin||0)>=10?'amber':'red'}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="AR Outstanding" value={fmtK(data.arAging?.reduce((s:number,a:any)=>s+parseFloat(a.outstanding||0),0))} icon="📤" color="amber"/>
            <KpiCard label="AP Outstanding" value={fmtK(data.apOutstanding)} icon="📥" color="red"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Section title="Monthly Revenue (12M)" icon="📈"><MiniBar data={data.monthlyTrend||[]} valueKey="revenue" labelKey="month" color="green" height={100}/></Section>
            <Section title="Revenue by Department" icon="🏥"><HBar data={data.revenueByDept||[]} valueKey="revenue" labelKey="department" color="blue"/></Section>
            <Section title="Collections by Mode" icon="💳"><HBar data={data.collections||[]} valueKey="collected" labelKey="payment_method" color="teal"/></Section>
          </div>
          {data.arAging?.length>0&&<Section title="AR Aging" icon="⏳" noPad>
            <STable headers={['Type','Count','Outstanding','0-30d','31-60d','61-90d','90d+']} rows={(data.arAging||[]).map((r:any)=>[r.ar_type,r.count,fmtK(r.total_outstanding),fmtK(r.overdue_0_30),fmtK(r.overdue_31_60),fmtK(r.overdue_61_90),fmtK(r.overdue_90_plus)])}/>
          </Section>}
        </div>)

        /* ════════════════ PATIENT ════════════════ */
        :view==='patient'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Growth Months" value={data.growth?.length||0} icon="📈" color="blue"/>
            <KpiCard label="Repeat Patients" value={fmt(data.repeatPatients,'number')} icon="🔄" color="teal"/>
            <KpiCard label="Cities Covered" value={data.city?.length||0} icon="📍" color="purple"/>
            <KpiCard label="Age Groups" value={data.ageGroup?.length||0} icon="👥" color="slate"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Section title="Monthly Patient Growth" icon="📈"><MiniBar data={data.growth||[]} valueKey="new_patients" labelKey="month" color="blue" height={100}/></Section>
            <Section title="Gender Distribution" icon="👥">
              <div className="space-y-2">
                {(data.gender||[]).map((g:any)=>{const total=data.gender?.reduce((s:number,x:any)=>s+(+x.count||0),0)||1;const p=Math.round((+g.count/total)*100);return<div key={g.gender} className="flex items-center gap-2 text-sm"><span className="flex-1 text-slate-600">{g.gender||'Unknown'}</span><span className="font-bold">{g.count}</span><span className="text-slate-400 w-8">{p}%</span></div>;})}
              </div>
            </Section>
            <Section title="Age Group Distribution" icon="📊"><HBar data={data.ageGroup||[]} valueKey="count" labelKey="age_group" color="purple"/></Section>
            <Section title="Top Cities" icon="📍"><HBar data={data.city||[]} valueKey="count" labelKey="city" color="teal"/></Section>
          </div>
        </div>)

        /* ════════════════ DOCTOR ════════════════ */
        :view==='doctor'?(<div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Section title="Top Doctors by Consultations" icon="👨‍⚕️"><HBar data={data.consultations||[]} valueKey="consultations" labelKey="doctor_id" color="blue"/></Section>
            <Section title="Top Doctors by Admissions" icon="🛏️"><HBar data={data.admissions||[]} valueKey="admissions" labelKey="doctor_id" color="purple"/></Section>
            <Section title="Top Doctors by Revenue" icon="💰"><HBar data={data.revenue||[]} valueKey="revenue" labelKey="doctor_id" color="green"/></Section>
          </div>
          <Section title="Department Performance" icon="🏥" noPad>
            <STable headers={['Department','Consultations','Revenue']} rows={(data.byDepartment||[]).map((r:any)=>[r.department||'—',r.consultations,fmtK(r.revenue)])}/>
          </Section>
        </div>)

        /* ════════════════ CLINICAL ════════════════ */
        :view==='clinical'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Readmissions" value={fmt(data.readmissions,'number')} icon="🔄" color="amber"/>
            <KpiCard label="Mortality Rate" value={data.mortalityRate+'%'} icon="📊" color="red"/>
            <KpiCard label="Avg LOS (days)" value={data.avgLos||'—'} icon="📅" color="blue"/>
            <KpiCard label="Top Diagnoses" value={data.topDiagnoses?.length||0} icon="🩺" color="teal"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Top 20 Diagnoses (ICD-11)" icon="🩺" noPad>
              <STable headers={['ICD-11','Diagnosis','Count']} rows={(data.topDiagnoses||[]).slice(0,15).map((r:any)=>[<span key={r.icd10_code} className="font-mono text-xs bg-blue-50 text-[#02357A] px-1 rounded">{r.icd10_code||'—'}</span>,r.diagnosis,r.count])}/>
            </Section>
            <Section title="Ward Performance" icon="🛏️" noPad>
              <STable headers={['Ward','Cases','Avg LOS']} rows={(data.byWard||[]).map((r:any)=>[r.ward_name,r.cases,r.avg_los+'d'])}/>
            </Section>
          </div>
        </div>)

        /* ════════════════ INSURANCE ════════════════ */
        :view==='insurance'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total Claims" value={fmt(data.claims?.total,'number')} icon="📋" color="blue"/>
            <KpiCard label="Total Claimed" value={fmtK(data.claims?.claimed)} icon="💰" color="amber"/>
            <KpiCard label="Approved Amount" value={fmtK(data.claims?.approved)} icon="✅" color="green"/>
            <KpiCard label="Rejection Rate" value={data.rejectionRate+'%'} icon="❌" color="red"/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
            <KpiCard label="Pending Count" value={fmt(data.pending?.count,'number')} icon="⏳" color="amber"/>
            <KpiCard label="Pending Amount" value={fmtK(data.pending?.amount)} icon="⏳" color="amber"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Claims by Status" icon="📊"><HBar data={data.byStatus||[]} valueKey="count" labelKey="status" color="blue"/></Section>
            <Section title="Top Insurers by Approved Amount" icon="🏦"><HBar data={data.byInsurer||[]} valueKey="approved" labelKey="insurance_name" color="green"/></Section>
          </div>
        </div>)

        /* ════════════════ BLOOD BANK ════════════════ */
        :view==='bloodbank'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Units Collected" value={fmt(data.collected,'number')} icon="🩸" color="red"/>
            <KpiCard label="Units Issued" value={fmt(data.issued,'number')} icon="📤" color="amber"/>
            <KpiCard label="Wastage" value={fmt(data.wastage,'number')+' units'} icon="⚠️" color="red"/>
            <KpiCard label="Near Expiry" value={fmt(data.nearExpiry?.length||0,'number')+' units'} icon="⏰" color="amber"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Current Stock by Blood Group" icon="🩸">
              <div className="grid grid-cols-4 gap-2 p-2">
                {(data.byGroup||[]).map((g:any)=>(
                  <div key={g.blood_group} className="bg-red-50 border border-red-200 p-3 rounded-lg text-center">
                    <p className="text-xl font-black text-red-700">{g.units}</p>
                    <p className="text-xs text-red-600 mt-0.5 font-semibold">{g.blood_group}</p>
                  </div>
                ))}
              </div>
            </Section>
            {data.nearExpiry?.length>0&&<Section title="Near Expiry (7 days)" icon="⚠️" noPad>
              <STable headers={['Blood Group','Component','Qty','Expiry']} rows={(data.nearExpiry||[]).map((e:any)=>[e.blood_group,e.component_type,e.quantity_units,e.expiry_date?.slice(0,10)])}/>
            </Section>}
          </div>
        </div>)

        /* ════════════════ INVENTORY ════════════════ */
        :view==='inventory'?(<div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Stock Value" value={fmtK(data.stockValue)} icon="📦" color="blue"/>
            <KpiCard label="Expiry Risk Items" value={fmt(data.nearExpiry?.length||0,'number')} icon="⚠️" color="amber"/>
            <KpiCard label="Low Stock Items" value={fmt(data.lowStock?.length||0,'number')} icon="🔴" color="red"/>
            <KpiCard label="Top Consumed" value={fmt(data.topConsumption?.length||0,'number')+' items'} icon="📊" color="teal"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Section title="Top Items by Consumption" icon="📊"><HBar data={data.topConsumption||[]} valueKey="consumed" labelKey="item_name" color="blue"/></Section>
            {data.lowStock?.length>0&&<Section title="Low Stock Alert" icon="🔴" noPad>
              <STable headers={['Item','Available','Reorder Level']} rows={(data.lowStock||[]).map((e:any)=>[e.item_name,<span key={e.item_name} className="text-red-600 font-bold">{e.quantity_available}</span>,e.reorder_level])}/>
            </Section>}
          </div>
          {data.nearExpiry?.length>0&&<Section title="Items Expiring Within 90 Days" icon="⚠️" noPad>
            <STable headers={['Item','Available','Expiry']} rows={(data.nearExpiry||[]).map((e:any)=>[e.item_name,e.quantity_available,e.expiry_date?.slice(0,10)])}/>
          </Section>}
        </div>)

        /* fallback */
        :<div className="card p-12 text-center text-slate-400">Select a view</div>
      )}

      {!loading&&!data&&<div className="card p-12 text-center text-slate-400"><p className="text-5xl mb-3">📊</p><p className="font-medium">No data loaded. Click ↻ Refresh.</p></div>}
    </Layout>
  );
}
